import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Tooltip, Button } from '@mui/material';
import { ShowChart, ArrowUpward, ArrowDownward, ScatterPlot } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { investmentAPI } from '../../api/investmentAPI';
import TickerDetailView from '../TickerDetailView';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useProfile } from '../../contexts/ProfileContext';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';
import { useMemo } from 'react';
import { getSignalColor, getTrendColor, baseColors } from '../../theme/themesV3';
import { useTheme, useMediaQuery } from '@mui/material';
import { StockChart } from '../charts/StockChart';
import { alpha } from '@mui/material/styles';
import { 
  BarChart as MarketCapIcon,
  KeyboardDoubleArrowRight as ViewMoreIcon
 } from '@mui/icons-material';

// Styled component for section headers
const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ETF_LIST = ['SPY', 'QQQ', 'IWM', 'DIA'];

// Create a mapper function similar to TickerDetailView to standardize ratings
const mapRatingToSignal = (rating: string | undefined): string => {
  if (!rating) return 'Hold';
  const normalized = rating.toLowerCase().trim();
  
  if (normalized.includes('strong') && normalized.includes('buy')) return 'Strong Buy';
  if (normalized === 'buy' || (normalized.includes('buy') && !normalized.includes('strong'))) return 'Buy';
  if (normalized.includes('hold') || normalized.includes('neutral')) return 'Hold';
  if (normalized === 'sell' || (normalized.includes('sell') && !normalized.includes('strong'))) return 'Sell';
  if (normalized.includes('strong') && normalized.includes('sell')) return 'Strong Sell';
  
  return 'Hold'; // Default
};

// Update getRatingColor to use the mapper
const getRatingColor = (rating: string | undefined) => {
  // Map the rating to standardized format and get color from themesV3
  return getSignalColor(mapRatingToSignal(rating));
};

interface TimeframeSignalCardProps {
  timeframe: string;
  rating: string;
  target: number;
  currentPrice: number;
}

const TimeframeSignalCard: React.FC<TimeframeSignalCardProps> = ({ 
  timeframe, 
  rating, 
  target,
  currentPrice 
}) => {
  const theme = useTheme();
  const isPositive = target > currentPrice;
  const percentChange = currentPrice > 0 ? (((target / currentPrice) - 1) * 100).toFixed(1) : '0.0';
  
  // Get proper rating color from themesV3
  const ratingColor = getRatingColor(rating);
  
  return (
    <Box sx={{ 
      flex: 1,
      minWidth: '120px',
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 1,
      overflow: 'hidden',
      height: '100%',
      borderTop: 4,
      borderColor: ratingColor // Use themesV3 color through getRatingColor
    }}>
      {/* Header with Timeframe and Rating */}
      <Box sx={{ 
        p: 1.5,
        pb: 0.5,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {timeframe}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            py: 0.5,
            px: 1.5,
            borderRadius: 4,
            bgcolor: ratingColor, // Use themesV3 color through getRatingColor
            color: 'common.white',
            fontSize: '0.85rem',
            fontWeight: 600
          }}
        >
          {rating}
        </Box>
      </Box>
      
      {/* Content */}
      <Box sx={{ p: 1.5, pt: 0.5 }}>
        {/* Price Target */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Price Target
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              ${target.toFixed(2)}
            </Typography>
          </Box>
          
          {/* Expected Return */}
          <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 1,
              fontSize: '0.875rem',
              bgcolor: theme.palette.mode === 'dark' ? 
                alpha(theme.palette.background.default, 0.4) : 
                alpha(theme.palette.background.default, 0.6),
              borderRadius: 1,
              py: 0.5,
              px: 1
            }}
          >
            <Typography 
              variant="body2" 
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.primary',
                fontWeight: 'medium'
              }}
            >
              {isPositive ? (
                <ArrowUpward fontSize="small" sx={{ mr: 0.5, color: getTrendColor(1, theme) }} />
              ) : (
                <ArrowDownward fontSize="small" sx={{ mr: 0.5, color: getTrendColor(-1, theme) }} />
              )}
              Expected: {isPositive ? '+' : ''}{percentChange}%
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  color: string;
  icon?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, icon }) => (
  <Box
    sx={{
      bgcolor: color,
      color: '#fff',
      borderRadius: 2,
      px: 2,
      py: 1.5,
      minWidth: 120,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      boxShadow: 2,
      mb: { xs: 1, md: 0 },
    }}
  >
    <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {icon}
      {value}
    </Typography>
  </Box>
);

const MarketETFsAnalysis: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tickerData, setTickerData] = useState<Record<string, any>>({});
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), [addStatus, removeStatus]);
  const { currentProfile } = useProfile();  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    let mounted = true;
    const abortController = new AbortController();
    
    const fetchTickerData = async () => {
      try {
        const marketETFs = ['SPY', 'QQQ', 'IWM', 'DIA'];
        const allData: Record<string, any> = await api.getMultiTickerData(marketETFs, currentProfile?.investment_profile_template || null);
        
        if (mounted) {
          setTickerData(allData);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          console.error('Error fetching ticker data:', err);
          setError('Failed to fetch ETF data');
          setLoading(false);
        }
      }
    };

    fetchTickerData();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, [api, currentProfile]); 

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <MarketCapIcon color="primary" sx={{ fontSize: 30, mr: 2 }} />
        <Typography variant="h4" fontWeight="bold" color="primary">
          Market ETFs Analysis
        </Typography>
      </Box>
      
      {/* Grid of Large ETF Cards (Dashboard Style) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 4,
        }}
      >
        {ETF_LIST.map((ticker) => (
          <Box key={ticker} sx={{ minHeight: 480 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: 3,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
                overflow: 'hidden'
              }}
              onClick={() => setSelectedTicker(ticker)}
            >
              {/* Header with Ticker and Price */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider'
              }}>
                <Typography variant="h4" fontWeight={700} color={baseColors.blue}>{ticker}</Typography>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Current Price</Typography>
                  <Typography variant="h5" color="text.primary" fontWeight="bold">
                    ${tickerData[ticker]?.close?.toFixed(2) || '--'}
                  </Typography>
                </Box>
              </Box>
              
              {/* Interactive Chart with note about positioning */}
              {tickerData[ticker]?.historical_ratings && (
                <Box sx={{ mb: 2 }}>
                  <StockChart 
                    data={tickerData[ticker].historical_ratings} 
                    height={320} 
                    isTrimmed={true} 
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                    Hover over chart for details • Exact price targets shown below
                  </Typography>
                </Box>
              )}
              
              {/* Signals and Targets */}
              <Box sx={{ my: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>Signals & Price Targets</Typography>
                {tickerData[ticker] && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <TimeframeSignalCard
                      timeframe="Short Term"
                      rating={tickerData[ticker]?.ShortTermRating?.toUpperCase() || 'N/A'} 
                      target={tickerData[ticker]?.ShortTermPriceTarget || 0}
                      currentPrice={tickerData[ticker]?.close || 0}
                    />
                    <TimeframeSignalCard
                      timeframe="Mid Term"
                      rating={tickerData[ticker]?.SwingTradeRating?.toUpperCase() || 'N/A'} 
                      target={tickerData[ticker]?.SwingTradePriceTarget || 0}
                      currentPrice={tickerData[ticker]?.close || 0}
                    />
                    <TimeframeSignalCard
                      timeframe="Long Term"
                      rating={tickerData[ticker]?.LongTermRating?.toUpperCase() || 'N/A'} 
                      target={tickerData[ticker]?.LongTermPriceTarget || 0}
                      currentPrice={tickerData[ticker]?.close || 0}
                    />
                  </Box>
                )}
              </Box>
              
              {/* Summary */}
              <Box sx={{ mt: 'auto', pt: 1, borderTop: 1, borderColor: 'divider', textAlign: 'right' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'left' }}>AI Analysis Summary</Typography>
                <Box sx={{ position: 'relative' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      display: '-webkit-box', 
                      textAlign: 'left',
                      WebkitLineClamp: 2, 
                      WebkitBoxOrient: 'vertical', 
                      overflow: 'hidden',
                      mb: 0.5
                    }}
                  >
                    {tickerData[ticker]?.Summary?.replace(/"/g, '') || 'No summary available'}
                  </Typography>
                  <Button 
                    //variant="text" 
                    //color="info.main" 
                    endIcon={<ViewMoreIcon />}
                    sx={{ 
                      fontStyle: 'italic', 
                      //textDecoration: 'underline',
                      //display: 'flex',
                      //justifyContent: 'flex-end',
                      cursor: 'pointer'
                    }} >
                    Click for more details
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      
      {/* Ticker Detail View Popup */}
      {selectedTicker && tickerData[selectedTicker] && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          bgcolor: 'rgba(0,0,0,0.5)', zIndex: 1400, overflow: 'auto'
        }}>
          <Box sx={{ position: 'relative', maxWidth: '90%', maxHeight: '90%', overflow: 'auto', bgcolor: 'background.paper', m: 'auto', mt: 4, p: 3, borderRadius: 2 }}>
            <IconButton
              onClick={() => setSelectedTicker(null)}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <TickerDetailView tickerInfo={tickerData[selectedTicker]} ticker={selectedTicker as string} onBack={() => setSelectedTicker(null)} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MarketETFsAnalysis;